import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

// Sample icons - in a real implementation, these would be imported from react-icons or custom SVGs
const DataSourceIcon = () => (
  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
  </div>
);

const AutoDiscoveryIcon = () => (
  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  </div>
);

const ProfilingIcon = () => (
  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  </div>
);

const LineageIcon = () => (
  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center text-yellow-600">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  </div>
);

const QualityIcon = () => (
  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center text-red-600">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  </div>
);

const AIIcon = () => (
  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  </div>
);

const FeatureCard = ({ icon, title, description, delay = 0, link }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6, delay }}
      className="card flex flex-col h-full"
    >
      <div>
        <div className="mb-4">{icon}</div>
        <h3 className="text-xl font-bold mb-3">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
      {link && (
        <div className="mt-auto pt-3 text-right">
          <Link 
            to={link} 
            className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
          >
            Learn More
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      )}
    </motion.div>
  );
};

const Features = () => {
  return (
    <section id="features" className="section py-20 bg-white">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-4xl font-bold mb-4"
          >
            Complete <span className="text-gradient">Data Observability</span> Features
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Qupid offers a comprehensive set of tools to ensure your data remains reliable, 
            trustworthy, and ready for business-critical decisions.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            icon={<DataSourceIcon />}
            title="Data Source Integration"
            description="Connect to a wide range of data sources including RDBMS, NoSQL, Analytics platforms, DWH solutions, Kafka, and Snowflake."
            delay={0.1}
            link="/features/data-sources"
          />
          
          <FeatureCard
            icon={<AutoDiscoveryIcon />}
            title="Auto Discovery"
            description="Automatically discover metadata from your sources including schemas, databases, tables, columns, jobs, and BI dashboards."
            delay={0.2}
            link="/features/auto-discovery"
          />
          
          <FeatureCard
            icon={<ProfilingIcon />}
            title="Advanced Profiling"
            description="Get immediate insights into your data's shape with powerful profiling tools and interactive SQL exploration."
            delay={0.3}
            link="/features/profiling"
          />
          
          <FeatureCard
            icon={<QualityIcon />}
            title="Smart Quality Checks"
            description="Implement no-code quality rules covering completeness, freshness, schema integrity, uniqueness, validity, and volumes."
            delay={0.4}
            link="/features/quality-checks"
          />
          
          <FeatureCard
            icon={<LineageIcon />}
            title="Advanced Lineage"
            description="Track data flow from dashboards to tables with OpenLineage-compatible lineage for root cause and impact analysis."
            delay={0.5}
            link="/features/lineage"
          />
          
          <FeatureCard
            icon={<AIIcon />}
            title="AI-Powered Platform"
            description="Leverage anomaly detection, smart recommendations, Qupid Assistant, and intelligent root cause analysis algorithms."
            delay={0.6}
            link="/features/ai-platform"
          />
        </div>
        
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          {/* <a href="/features" className="button button-primary">View All Features</a> */}
        </motion.div>
      </div>
    </section>
  );
};

export default Features; 